import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  ListItem,
  Multiselect,
  Radio,
  Select,
  SelectItem,
  Stack,
  Switch,
  Text,
  Title,
} from "@skailer-com/uikit";
import useApprovalPolicyHook from "@/hooks/use-approval-policy.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useApprovalCategoryById } from "@/queries/approval/use-approval-categories.query.ts";
import {
  useApprovalDelete,
  useApprovalPolicyDetails,
  useApprovalPolicySave,
} from "@/queries/approval/use-approval-policies.query.ts";
import { useTimeOffCategoriesSearchInline } from "@/queries/use-time-off.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  getApprovalPolicyAssignToGroups,
  ApiApprovalCategoryType,
  ApiApprovalPolicyType,
  ApprovalPolicyFlowType,
  ApprovalPolicySubstitutionType,
} from "@/types/approvals/approval-policy.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { Rule, RuleApplySignType, RuleConditionType } from "@/types/role.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { RuleEditModal } from "../../roles/edit/widgets/RuleEditModal.tsx";

export function ApprovalPolicyEdit() {
  const { t } = useTranslation();
  const requiredMessage = t("errors:field_required");
  const ApprovalPolicyObject = z
    .object({
      title: z.string().min(1, requiredMessage),
      assign_to_groups: ruleGroupsSchema(),
      flow_rules: z.array(z.custom<Rule>(), { error: requiredMessage }),
      flow_processing_type: z.enum(ApiApprovalPolicyType, { error: requiredMessage }),
      flow_type: z.enum(ApprovalPolicyFlowType, { error: requiredMessage }),
      flow_skip: z.boolean({ error: requiredMessage }),
      substitution_enabled: z.boolean({ error: requiredMessage }),
      substitution_type: z.enum(ApprovalPolicySubstitutionType).nullable().optional(),
      substitution_rule: z.custom<Rule>().nullable().optional(),
      time_off_category_ids: z.array(z.number()),
    })
    .superRefine(({ flow_rules, flow_type }, context) => {
      if (flow_type === ApprovalPolicyFlowType.sequence_approval || flow_rules.length <= 1) return;

      flow_rules.forEach((rule, index) => {
        if (rule.condition_type === RuleConditionType.employee && rule.condition_ids.length > 1) {
          context.addIssue({
            code: "custom",
            path: ["flow_rules", index],
            message: t("approval:multiple_approvers_require_sequence"),
          });
        }
      });
    });
  const assignToConditionTypes = useRuleConditionTypes(RuleRegistryPlace.approvalPolicyAssignTo);
  const flowRuleConditionTypes = useRuleConditionTypes(RuleRegistryPlace.approvalPolicyFlowRules);
  const substitutionRuleConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.approvalPolicySubstitutionRule
  );
  const { id: categoryId, policyId } = useParams();
  const isEdit = !!policyId;
  useTitle(isEdit ? t("common:edit") : t("common:create_new"), t("approval:settings_title"));
  const {
    handleSubmit,
    control,
    register,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof ApprovalPolicyObject>>({
    mode: "onSubmit",
    resolver: zodResolver(ApprovalPolicyObject),
    defaultValues: {
      title: t("approval:default_title"),
      assign_to_groups: [],
      flow_rules: [],
      flow_skip: false,
      flow_processing_type: ApiApprovalPolicyType.any,
      flow_type: ApprovalPolicyFlowType.parallel_approval,
      substitution_enabled: false,
      substitution_type: null,
      substitution_rule: null,
      time_off_category_ids: [],
    },
  });
  const navigate = useNavigate();
  const { openModal } = useModals();
  const { getFlowRuleTitleByType } = useApprovalPolicyHook();
  const policySave = useApprovalPolicySave(control);
  const policyDelete = useApprovalDelete();
  const { data: approvalPolicy } = useApprovalPolicyDetails(
    policyId ? Number(policyId) : undefined
  );
  const { data: category } = useApprovalCategoryById(Number(categoryId));
  const type = isEdit ? approvalPolicy?.category : category?.type;
  const isTimeOffCategory = type === ApiApprovalCategoryType.time_off;
  const timeOffCategories = useTimeOffCategoriesSearchInline({ enabled: isTimeOffCategory });
  const {
    fields: flowFields,
    append: flowRuleAppend,
    remove: flowRuleRemove,
    update: flowRuleUpdate,
    move: flowRuleMove,
  } = useFieldArray({
    control,
    name: "flow_rules",
  });
  const flowType = useWatch({ control, name: "flow_type" });

  const shouldRenderAssignEmployees = type !== ApiApprovalCategoryType.offer;

  useEffect(() => {
    if (!isTimeOffCategory) {
      setValue("time_off_category_ids", [], { shouldValidate: true });
    }
  }, [isTimeOffCategory, setValue]);

  const handleEditFlowRule = useCallback(
    (rule?: Rule | undefined, index?: number | undefined) => {
      openModal(
        RuleEditModal,
        {
          rule,
          popupTitle: index ? t("approval:edit_approval_step") : t("approval:add_approval_step"),
          conditionTypes: flowRuleConditionTypes.conditionTypes,
          forceSign: RuleApplySignType.include,
          isSingleSelect: true,
          multiSelectConditionTypes: [RuleConditionType.employee],
          onModified(flowRule: Rule) {
            if (index !== undefined) {
              flowRuleUpdate(index, flowRule);
            } else {
              flowRuleAppend(flowRule);
            }
          },
          onDelete() {
            flowRuleRemove(index);
          },
        },
        {
          size: "lg",
        }
      );
    },
    [
      flowRuleAppend,
      flowRuleConditionTypes.conditionTypes,
      flowRuleRemove,
      flowRuleUpdate,
      openModal,
      t,
    ]
  );

  useEffect(() => {
    if (flowFields.length <= 1) {
      setValue("flow_type", ApprovalPolicyFlowType.parallel_approval, { shouldValidate: true });
      setValue("flow_processing_type", ApiApprovalPolicyType.full, { shouldValidate: true });
    }
  }, [flowFields.length, setValue]);

  useEffect(() => {
    if (flowType === ApprovalPolicyFlowType.sequence_approval) {
      setValue("flow_processing_type", ApiApprovalPolicyType.full, { shouldValidate: true });
    }
  }, [flowType, setValue]);

  const substitutionEnabled = useWatch({ control, name: "substitution_enabled" });
  const substitutionType = useWatch({
    control,
    name: "substitution_type",
  });

  useEffect(() => {
    if (!substitutionEnabled) {
      setValue("substitution_type", null, { shouldValidate: true });
      setValue("substitution_rule", null, { shouldValidate: true });
    }
  }, [substitutionEnabled, setValue]);

  const handleDelete = () => {
    if (!approvalPolicy) return;
    if (policyDelete.isPending) return;

    openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await policyDelete.mutateAsync({
            policyId: approvalPolicy.id,
            categoryId: Number(categoryId),
          });

          navigate(`/settings/approval-categories/${categoryId}`);
        } catch {
          return;
        }
      },
    });
  };

  const getSubstitutionTypeSelectItems = (): SelectItem[] => {
    return [
      {
        id: ApprovalPolicySubstitutionType.skip,
        title: t("common:skip"),
      },
      {
        id: ApprovalPolicySubstitutionType.transfer,
        title: t("common:transfer"),
      },
    ];
  };

  const onSubmit = handleSubmit((policyData) => {
    if (!category) return;

    policySave.mutate(
      {
        id: policyId ? Number(policyId) : 0,
        payload: {
          title: policyData.title,
          assign_to_groups: policyData.assign_to_groups.filter((group) => group.rules.length > 0),
          flow_rules: policyData.flow_rules,
          flow_skip: policyData.flow_skip,
          flow_processing_type: policyData.flow_processing_type,
          flow_type: policyData.flow_type,
          category_id: category.id,
          substitution_enabled: policyData.substitution_enabled,
          substitution_type: policyData.substitution_enabled
            ? (policyData.substitution_type ?? null)
            : null,
          substitution_rule: policyData.substitution_enabled
            ? (policyData.substitution_rule ?? null)
            : null,
          time_off_category_ids: isTimeOffCategory ? policyData.time_off_category_ids : [],
        },
      },
      {
        onSuccess(createdPolicy) {
          analyticsService.trackEvent(
            isEdit ? analyticEvents.approvals.editedPolicy : analyticEvents.approvals.createdPolicy,
            {
              [analyticProperties.id]: createdPolicy.id,
            }
          );

          navigate(`/settings/approval-categories/${categoryId}`);
        },
      }
    );
  });

  useEffect(() => {
    if (!approvalPolicy) return;

    reset({
      title: approvalPolicy.title,
      assign_to_groups: getApprovalPolicyAssignToGroups(approvalPolicy).map((group) => ({
        rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
      })),
      flow_rules: approvalPolicy.flow_rules,
      flow_skip: approvalPolicy.flow_skip,
      flow_processing_type: approvalPolicy.flow_processing_type,
      flow_type: approvalPolicy.flow_type,
      substitution_enabled: approvalPolicy.substitution_enabled,
      substitution_type: approvalPolicy.substitution_enabled
        ? (approvalPolicy.substitution_type ?? null)
        : null,
      substitution_rule: approvalPolicy.substitution_enabled
        ? (approvalPolicy.substitution_rule ?? null)
        : null,
      time_off_category_ids: approvalPolicy.time_off_category_ids ?? [],
    });
  }, [approvalPolicy]);

  useEffect(() => {
    if (substitutionType === ApprovalPolicySubstitutionType.transfer) {
      const current = getValues("substitution_rule");
      if (!current) {
        setValue(
          "substitution_rule",
          {
            condition_type: RuleConditionType.direct_manager,
            sign: RuleApplySignType.include,
            condition_ids: [],
            condition_entities: [],
            value_ids: [],
            value_entities: [],
          },
          { shouldValidate: true }
        );
      }
    } else {
      setValue("substitution_rule", null, { shouldValidate: true });
    }
  }, [substitutionType, getValues, setValue]);

  const handleEditSubstitutionRule = useCallback(
    (rule?: Rule | null) => {
      openModal(
        RuleEditModal,
        {
          rule: rule ?? {
            condition_type: RuleConditionType.direct_manager,
            sign: RuleApplySignType.include,
            condition_ids: [],
            condition_entities: [],
            value_ids: [],
            value_entities: [],
          },
          popupTitle: t("approval:edit_substitution_rule"),
          conditionTypes: substitutionRuleConditionTypes.conditionTypes,
          forceSign: RuleApplySignType.include,
          isSingleSelect: true,
          onModified(updated: Rule) {
            setValue(
              "substitution_rule",
              {
                ...updated,
              },
              { shouldValidate: true }
            );
          },
          onDelete() {},
          canDelete: false,
        },
        { size: "lg" }
      );
    },
    [openModal, setValue, substitutionRuleConditionTypes.conditionTypes, t]
  );

  const substitutionRule = useWatch({ control, name: "substitution_rule" });

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;

    flowRuleMove(source.index, destination.index);
  };

  return (
    <Page>
      <Page.Header
        title={t(isEdit ? "common:edit" : "common:create_new")}
        showBack={true}
        navigateTo={`/settings/approval-categories/${categoryId}`}
      />
      <Page.Content>
        <Stack className={"px-16"} gap={"2xl"}>
          <Stack gap={"lg"}>
            <Stack>
              <Input
                type={"text"}
                {...register("title")}
                error={errors.title?.message}
                label={t("common:title")}
                size={"lg"}
              />
              <Title
                header={t("approval:steps_title")}
                caption={t("approval:steps_caption")}
                paddingTop={true}
                paddingBottom={true}
                size={"lg"}
              />
              <Box variant={"border"}>
                <Stack className={"mb-4"}>
                  {getValues("flow_rules").length === 0 && t("common:empty")}
                  {flowFields.length > 0 && (
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="flow_rules">
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            {flowFields.map((rule, index) => (
                              <Draggable key={rule.id} draggableId={String(rule.id)} index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={provided.draggableProps.style}
                                    className="flex justify-between min-h-14 items-center"
                                  >
                                    <div
                                      className="w-10 flex items-center justify-center"
                                      {...provided.dragHandleProps}
                                    >
                                      <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                                    </div>

                                    <Stack className="flex-1" gap="xs">
                                      <ListItem
                                        key={`flow_rule_${index}`}
                                        title={getFlowRuleTitleByType(
                                          rule.condition_type,
                                          rule.condition_entities
                                        )}
                                        caption={
                                          rule.condition_type === RuleConditionType.employee &&
                                          rule.condition_ids.length > 1
                                            ? `${t("approval:step", { step: index + 1 })} · ${t(
                                                "approval:any_selected_employee_can_approve"
                                              )}`
                                            : t("approval:step", { step: index + 1 })
                                        }
                                        valueSlot={
                                          <div
                                            className={
                                              "underline hover:no-underline cursor-pointer"
                                            }
                                            onClick={() => {
                                              handleEditFlowRule(rule, index);
                                            }}
                                          >
                                            {t("common:edit")}
                                          </div>
                                        }
                                      />
                                      {errors.flow_rules?.[index]?.message && (
                                        <Text className="text-danger text-sm">
                                          {errors.flow_rules[index].message}
                                        </Text>
                                      )}
                                    </Stack>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </Stack>
                <Button
                  onClick={() => {
                    handleEditFlowRule();
                  }}
                  variant={"secondary"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                >
                  {t("common:add")}
                </Button>
              </Box>
            </Stack>

            <Stack gap={"md"}>
              <Title header={t("common:settings")} size={"xs"} paddingBottom={true} />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                    size={"sm"}
                    title={t("approval:flow_skip_title")}
                    description={t("approval:flow_skip_caption")}
                  />
                )}
                control={control}
                name={"flow_skip"}
              />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                    size={"sm"}
                    title={t("approval:substitution_enabled_title")}
                    description={t("approval:substitution_enabled_caption")}
                  />
                )}
                control={control}
                name={"substitution_enabled"}
              />
              {substitutionEnabled && (
                <Box variant={"border"}>
                  <Stack direction="horizontal" gap={"md"}>
                    <Box className="basis-1/2 min-w-0">
                      <Controller
                        key={"substitution_type"}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            label={t("approval:substitution_type_caption")}
                            options={getSubstitutionTypeSelectItems()}
                            emptyTitle={t("common:select")}
                            value={value ?? ""}
                            onChange={(v) => {
                              onChange(v ?? null);
                            }}
                            error={errors.substitution_type?.message as string}
                          />
                        )}
                        control={control}
                        name={"substitution_type"}
                      />
                    </Box>
                    <Box className="basis-1/2 min-w-0">
                      {substitutionType === ApprovalPolicySubstitutionType.transfer &&
                        substitutionRule && (
                          <ListItem
                            key={"substitution_rule"}
                            title={getFlowRuleTitleByType(
                              substitutionRule.condition_type,
                              substitutionRule.condition_entities
                            )}
                            valueSlot={
                              <div
                                className={"underline hover:no-underline cursor-pointer"}
                                onClick={() => handleEditSubstitutionRule(substitutionRule)}
                              >
                                {t("common:edit")}
                              </div>
                            }
                          />
                        )}
                    </Box>
                  </Stack>
                </Box>
              )}
            </Stack>

            <Stack gap={"md"}>
              {flowFields.length > 1 && (
                <>
                  <Title header={t("approval:mandatory_approvals_title")} size={"xs"} />
                  <Box variant={"border"}>
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <>
                          <Radio
                            value={ApprovalPolicyFlowType.parallel_approval}
                            checked={value == ApprovalPolicyFlowType.parallel_approval}
                            onChange={() => {
                              onChange(ApprovalPolicyFlowType.parallel_approval);
                            }}
                            title={t("approval:parallel_approval_title")}
                            caption={t("approval:parallel_approval_caption")}
                          />
                          <Radio
                            value={ApprovalPolicyFlowType.sequence_approval}
                            checked={value == ApprovalPolicyFlowType.sequence_approval}
                            onChange={() => {
                              onChange(ApprovalPolicyFlowType.sequence_approval);
                            }}
                            title={t("approval:sequence_approval_title")}
                            caption={t("approval:sequence_approval_caption")}
                          />
                        </>
                      )}
                      control={control}
                      name={"flow_type"}
                    />
                  </Box>
                  {flowType === ApprovalPolicyFlowType.parallel_approval && (
                    <Box variant={"border"}>
                      <Controller
                        render={({ field: { value, onChange } }) => (
                          <>
                            <Radio
                              value={ApiApprovalPolicyType.full}
                              checked={value == ApiApprovalPolicyType.full}
                              onChange={() => {
                                onChange(ApiApprovalPolicyType.full);
                              }}
                              title={t("approval:all_approvals_required_title")}
                              caption={t("approval:all_approvals_required_caption")}
                            />
                            <Radio
                              value={ApiApprovalPolicyType.any}
                              checked={value == ApiApprovalPolicyType.any}
                              onChange={() => {
                                onChange(ApiApprovalPolicyType.any);
                              }}
                              title={t("approval:any_approvals_required_title")}
                              caption={t("approval:any_approvals_required_caption")}
                            />
                          </>
                        )}
                        control={control}
                        name={"flow_processing_type"}
                      />
                    </Box>
                  )}
                </>
              )}

              {isTimeOffCategory && (
                <Stack>
                  <Title
                    header={t("approval:time_off_categories_title")}
                    caption={t("approval:time_off_categories_caption")}
                    size={"xs"}
                    paddingBottom
                  />
                  <Controller
                    control={control}
                    name={"time_off_category_ids"}
                    render={({ field: { value, onChange } }) => (
                      <Multiselect<SelectItem>
                        emptyTitle={t("common:select")}
                        values={value}
                        options={timeOffCategories.data ?? []}
                        searchOptions={timeOffCategories.options}
                        onSearchOptions={timeOffCategories.search}
                        loadOptionsOnOpen
                        searchable
                        isClearable
                        isMulti
                        isLoading={timeOffCategories.isLoading}
                        onChange={onChange}
                        error={errors.time_off_category_ids?.message}
                      />
                    )}
                  />
                </Stack>
              )}
              {shouldRenderAssignEmployees && (
                <>
                  <Title
                    header={t("core:assign_employees")}
                    paddingTop={true}
                    paddingBottom={true}
                    size={"lg"}
                  />

                  <RuleGroupsEditor
                    control={control}
                    fieldName={"assign_to_groups"}
                    setValue={setValue}
                    place={RuleRegistryPlace.approvalPolicyAssignTo}
                    conditionTypes={assignToConditionTypes.conditionTypes}
                    error={errors.assign_to_groups?.message}
                  />
                </>
              )}
            </Stack>
          </Stack>
          <Stack className={"pt-(--gap-md)"} gap={"md"}>
            <ServerErrorField errors={errors} />
            <Button size={"lg"} type={"submit"} onClick={onSubmit} isLoading={policySave.isPending}>
              {t("common:save")}
            </Button>
            {isEdit && (
              <Button
                variant={"danger"}
                size={"lg"}
                type={"submit"}
                onClick={handleDelete}
                isLoading={policyDelete.isPending}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
              >
                {t("common:delete")}
              </Button>
            )}
          </Stack>
        </Stack>
      </Page.Content>
    </Page>
  );
}
